import { useMemo } from "react";
import { CircularProgressbarWithChildren, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { MEAL_CONFIG, MEAL_TYPE_MAP } from "../../config/meal-config";
import { calculateEnergyProgress, getSelectedDate } from "../../helpers";
import useDiaryData from "../../hooks/data/diary/useDiaryData";
import useGetDesiredEnergy from "../../hooks/data/energy/useGetDesiredEnergy";
import useGetEnergyByPeriod from "../../hooks/data/energy/useGetEnergyByPeriod";
import useDiaryMeals from "../../hooks/diary/useDiaryMeals";
import useDiarySections from "../../hooks/diary/useDiarySections";
import useModalController from "../../hooks/modal/useModalController";
import useDateFromUrl from "../../hooks/utils/useDateFromUrl";
import ErrorMessage from "../ui/ErrorMessage";
import LoadingSpinner from "../ui/LoadingSpinner";
import ProgressBar from "../ui/ProgressBar";
import DiaryEnergyChart from "./DiaryEnergyChart";
import DiaryModal from "./DiaryModal";
import DiaryResultsList from "./DiaryResultsList";
import DiarySectionItem from "./DiarySectionItem";
import { DiarySectionsProvider } from "../../context/DiarySectionsContext";
import useDeleteFavoriteMeal from "../../hooks/data/meals/useDeleteFavoriteMeal";

const DiaryContent = ({ current_day }) => {
	const currentDay = useDateFromUrl(current_day);

	const { data, isLoading, error, isError } = useDiaryData(currentDay);

	// SAFE DEFAULTS

	const calories = data?.calories ?? { food: 0, exercise: 0 };
	const deficit = data?.deficit ?? 0;
	const energyConsumption = data?.energy_consumption ?? 0;
	const energyProgress = data?.energy_progress ?? { carbs: 0, protein: 0, fat: 0 };
	const macros = data?.macros;
	const exerciseSession = data?.exercise_session ?? null;
	const exercises = exerciseSession?.exercises ?? data?.exercises ?? [];

	// UI

	const { addMeal, meals, deletePlaceholder } = useDiaryMeals(data?.meals ?? {});

	const { deleteFavoriteMeal, isDeletingFavorite } = useDeleteFavoriteMeal(currentDay);

	// MODAL CONTROLLER

	const modalController = useModalController();

	// SECTIONS

	const { openSections, toggleSection, closeSection } = useDiarySections();

	// ENERGY DATA

	const { energyData, isLoadingEnergy, energyError, isErrorEnergy } = useGetEnergyByPeriod(
		currentDay,
		currentDay,
	);

	const { desiredData, isLoadingDesired, desiredError, isErrorDesired } = useGetDesiredEnergy();

	const getEnergyLabelsAndValues = (energyData) => {
		if (!energyData?.data) return { labels: [], values: [] };

		return {
			labels: Object.keys(energyData.data),
			values: Object.values(energyData.data),
		};
	};

	const energy = useMemo(() => getEnergyLabelsAndValues(energyData), [energyData]);
	const desiredEnergy = useMemo(() => getEnergyLabelsAndValues(desiredData), [desiredData]);

	const energyStats = useMemo(
		() =>
			calculateEnergyProgress({
				calories,
				deficit,
				energyConsumption,
			}),
		[calories, deficit, energyConsumption],
	);

    const showNutritionLink = `/matdagbok/naringsintag${getSelectedDate() ? `?start=${currentDay}&end=${currentDay}` : ''}`

	// HELPER

	const getMealDataByType = (type) => meals[MEAL_TYPE_MAP[type]] ?? [];

	if (isError) {
		console.error("Diary data error:", error);
		return <ErrorMessage message={error?.message} />;
	}

	if (isLoading || !data || !currentDay) return <LoadingSpinner />;

	return (
		<>
			{/* --- RESULT SECTION --- */}
			<div className="diary-result">
				<DiaryResultsList calorieTarget={energyStats.calorieTarget} calories={calories} />

				<div className="result-left-to-eat">
					<span className="bold-title">Kvar att äta</span>
					<div className="left-to-eat-progress-bar">
						<CircularProgressbarWithChildren
							value={energyStats.percentEaten}
							strokeWidth={6}
							styles={buildStyles({
								strokeLinecap: "butt",
								pathColor: energyStats.progressbarColor.path,
								trailColor: energyStats.progressbarColor.trail,
							})}
						>
							<div className="progress-bar-text">
								<span className="kcal-number">{energyStats.leftToEat}</span>
								<span>/KCAL</span>
							</div>
						</CircularProgressbarWithChildren>
					</div>
				</div>
			</div>

			{/* --- Progress carbs, protein, fat --- */}
			<ProgressBar macros={macros} limit={energyProgress} />

			{/* --- MEALS SECTION --- */}
			<div className="title-row">
				<h3 className="bold-title">Måltider</h3>
			</div>

			<div className="diary-meals">
				<ul>
					{Object.values(MEAL_CONFIG).map((config) => {
						const mealsOfType = getMealDataByType(config.type);
						const rows = mealsOfType.length > 0 ? mealsOfType : [null];

						return rows.map((meal, index) => {
							const slotIndex = `${config.type}-${index}`;
							const isDuplicate = index > 0;

							return (
								<DiarySectionItem
									key={meal?.meal_id ?? slotIndex}
									name={meal?.name}
									recipeSlug={meal?.recipe_slug}
									time={meal?.time ?? ""}
									entryType={config.type}
									mealId={meal?.meal_id ?? null}
									favoriteId={meal?.favorite_id ?? null}
									title={config.title}
									iconSrc={config.icon}
									bgColor={config.bg}
									items={meal?.items ?? []}
									displayIndex={meal?._displayIndex ?? null}
									slotIndex={slotIndex}
									isSectionOpen={Boolean(openSections[slotIndex])}
									onToggleSection={() => toggleSection(slotIndex)}
									openModal={modalController.open}
									addMeal={addMeal}
									deletePlaceholder={deletePlaceholder}
									deleteFavoriteMeal={deleteFavoriteMeal}
									isDeletingFavorite={isDeletingFavorite}
									isEmptySlot={!meal}
									isDuplicate={isDuplicate}
								/>
							);
						});
					})}
				</ul>
			</div>

			{/* --- EXERCISE SECTION --- */}
			<div className="title-row">
				<h3 className="bold-title">Motion</h3>
			</div>

			<div className="diary-exercise">
				<ul>
					<DiarySectionItem
						title="Motion"
						iconSrc="/assets/images/diary-icons/person-running.svg"
						bgColor="#95C12180"
						items={exercises}
						slotIndex="exercise"
						entryType="exercise"
						isSectionOpen={Boolean(openSections.exercise)}
						onToggleSection={() => toggleSection("exercise")}
						openModal={modalController.open}
						isExercise
					/>
				</ul>
			</div>

			<div className="title-row">
				<h3 className="bold-title">Energi från måltider</h3>
			</div>

			<div className="diary-energy-intro">
				<p>Här nedan visas dagens energiintag.</p>
				<p>Klicka på knappen för att se mer näringsinformation.</p>
				<div className="divider-line"></div>
			</div>

			<div className="energy-types multi-chart">
				<DiaryEnergyChart
					title="Min Energifördelning (E %)"
					placeholderText="Ingen energidata för denna dag."
					data={energy}
					isLoading={isLoadingEnergy}
					isError={isErrorEnergy}
					error={energyError}
				/>

				<DiaryEnergyChart
					title="Önskad fördelning (E %)"
					placeholderText="Ingen önskad energifördelning tillgänglig."
					data={desiredEnergy}
					isLoading={isLoadingDesired}
					isError={isErrorDesired}
					error={desiredError}
				/>
			</div>

			<a href={showNutritionLink} id="show-nutrition-details-btn">
				<img src="/assets/images/diary-icons/chart-line-up.svg" alt="" />
				Se detaljerat näringsintag
			</a>

			{modalController.isOpen && (
				<DiarySectionsProvider
					value={{
						openSections,
						toggleSection,
						closeSection,
					}}
				>
					<DiaryModal
						currentDay={currentDay}
						controller={modalController}
						meals={meals}
						exerciseSession={exerciseSession}
					/>
				</DiarySectionsProvider>
			)}
		</>
	);
};

export default DiaryContent;
